/**
 * Repco Import API Service
 *
 * Two endpoints, both scoped to a single job:
 *   POST /api/jobs/{jobUuid}/repco-import/preview  — multipart (csv + operationsText)
 *   POST /api/jobs/{jobUuid}/repco-import/commit   — JSON
 *
 * The preview endpoint runs the LLM and is intentionally separate from commit
 * so the user can review and edit the proposed work items before any DB write.
 */

import axiosInstance from './axiosInstance';

/**
 * Generate a preview of the work items that would be created from the
 * supplied Repco quote. No DB writes — pure read + LLM call.
 *
 * The CSV is the standard Repco SmartQuote export; operationsText is the
 * copy-pasted service-operations block from the Repco portal page.
 *
 * @param {string} jobUuid
 * @param {File}   csvFile
 * @param {string} operationsText
 * @returns {Promise<{
 *   jobUuid: string,
 *   quoteNumber: string,
 *   quoteReference: string,
 *   vehicleName: string,
 *   intervalsCovered: string[],
 *   proposedWorkItems: Array<{
 *     tempId: string,
 *     intervalLabel: string,
 *     intervalKm: number|null,
 *     intervalMonths: number|null,
 *     description: string,
 *     detailNotes: string,
 *     labourHours: string|number,
 *     labourRate: string|number,
 *     parts: Array<{ partNumber, description, quantity, unitPrice, lineTotal }>,
 *     estimatedTotal: string|number,
 *   }>,
 *   unmappedCsvRows: Array<{ partNumber, description, quantity, unitPrice, lineTotal, reason }>,
 *   warnings: string[],
 * }>}
 */
export const previewRepcoImportApi = async (jobUuid, csvFile, operationsText) => {
    const formData = new FormData();
    formData.append('csv', csvFile);
    formData.append('operationsText', operationsText);

    // The LLM call typically takes 5–15s. Bump the per-request timeout so we
    // don't see spurious aborts while the proposal is being generated.
    const response = await axiosInstance.post(
        `/jobs/${jobUuid}/repco-import/preview`,
        formData,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 90000,
        },
    );
    return response.data;
};

/**
 * Commit the (possibly user-edited) proposal. Creates JobWorkItem rows on
 * the job and optionally transitions CREATED → QUOTED.
 *
 * Returns the full updated Job (with workItems populated) so the caller can
 * refresh the job detail view directly from the response.
 *
 * @param {string} jobUuid
 * @param {{
 *   proposedWorkItems: Array<{
 *     intervalLabel: string,
 *     description: string,
 *     detailNotes: string,
 *     labourHours: number,
 *     labourRate: number,
 *     parts: Array<{ partNumber, description, quantity, unitPrice, lineTotal }>,
 *     confirmationRequired?: boolean,
 *     confirmationMessage?: string|null,
 *   }>,
 *   transitionToQuoted?: boolean,
 * }} payload
 */
export const commitRepcoImportApi = async (jobUuid, payload) => {
    const response = await axiosInstance.post(
        `/jobs/${jobUuid}/repco-import/commit`,
        payload,
    );
    return response.data;
};

const repcoImportApi = {
    previewRepcoImportApi,
    commitRepcoImportApi,
};

export default repcoImportApi;